import type { pages as de } from '../de/pages';

/** Texts of the pages: lists, detail page, checklist, proof and sources. */
export const pages: typeof de = {
  fields: {
    title: 'Title',
    buyer: 'Contracting authority',
    place: 'Place',
    state: 'Federal state',
    nuts: 'Region (NUTS)',
    deadline: 'Tender deadline',
    participationDeadline: 'Participation deadline',
    questionsDeadline: 'Deadline for questions',
    cpv: 'CPV codes',
    nature: 'Type',
    procedure: 'Procedure',
    regulation: 'Procurement law',
    estimatedValue: 'Estimated value',
    published: 'Published',
    noticeId: 'Notice',
    source: 'Source',
    status: 'Status',
    owner: 'Responsible',
    markedAt: 'Marked on',
    submittedAt: 'Submitted on',
    documentsUrl: 'Link to the tender documents',
    description: 'Description',
  },
  regions: {
    BW: 'Baden-Württemberg',
    BY: 'Bavaria',
    BE: 'Berlin',
    BB: 'Brandenburg',
    HB: 'Bremen',
    HH: 'Hamburg',
    HE: 'Hesse',
    MV: 'Mecklenburg-Western Pomerania',
    NI: 'Lower Saxony',
    NW: 'North Rhine-Westphalia',
    RP: 'Rhineland-Palatinate',
    SL: 'Saarland',
    SN: 'Saxony',
    ST: 'Saxony-Anhalt',
    SH: 'Schleswig-Holstein',
    TH: 'Thuringia',
  },
  natures: { works: 'Works', services: 'Services', supplies: 'Supplies' },
  regulations: { eu: 'EU-wide', national: 'National' },
  sourceNames: {
    oev: 'oeffentlichevergabe.de',
    nrw: 'NRW procurement portals',
    ted: 'TED (EU Official Journal)',
    berlin: 'Berlin procurement platform',
    vkberlin: 'Vergabekooperation Berlin',
    rib: 'RIB meinauftrag',
    bund: 'service.bund.de',
    manual: 'Entered by hand',
  },
  deadlines: {
    none: 'No deadline',
    passed: 'Passed',
    today: 'Ends today',
    daysLeft_one: '{{count}} day left',
    daysLeft_other: '{{count}} days left',
    participationShort: 'Participation:',
  },
  failures: {
    versionConflict: 'Someone else has changed this in the meantime.',
    notMarked: 'The tender is not in work.',
    notInReview: 'This is only possible while the tender is in review.',
    manualTender: 'This is not possible for a tender entered by hand.',
    notSubmitted: 'The tender has not been submitted yet.',
    tooManyMembers: 'As many people as possible are already involved.',
    tooManyItems: 'The checklist already has as many points as possible.',
    tooManyLinks: 'This point already has as many proofs as possible.',
    aiDisabled: 'AI is switched off for this function.',
    aiBudget: "The company's AI budget for this month is used up.",
    aiBusy: 'The AI is busy right now. Please try again in a moment.',
    aiFailed: 'The AI did not return a usable answer. Please try again.',
    aiUnavailable: 'The AI cannot be reached right now.',
    noDocuments: 'There are no documents in the folder yet. Upload them first.',
    aiFilesTooLarge:
      'The documents are too large for the AI. Remove files the summary does not need.',
    documentsNoLink: 'The tender has no link to its documents.',
    documentsHost:
      'The STAW App does not load anything from this portal itself. Download the documents there and upload them here.',
    documentsNotDirect:
      'The portal hands out the documents only after signing in or through a form. Download them there and upload them here.',
    documentsTooLarge: 'The documents are larger than 100 MB.',
    documentsUnavailable: 'The portal does not answer right now. Please try again later.',
    sharepointDenied: 'SharePoint refuses access. Administration has to check the connection.',
    sharepointUnavailable: 'SharePoint cannot be reached right now.',
    sharepointDisabled: 'The SharePoint connection is switched off.',
    invalidInput: 'Please check your input.',
    notFound: 'This no longer exists.',
    forbidden: 'You do not have permission for this.',
    conflict: 'This does not match the current state. Please reload the page.',
    rateLimited: 'Too many requests. Please wait a moment.',
  },
  cpv: {
    add: 'Add CPV code',
    search: 'Search code or term',
    empty: 'No matching code',
    remove: 'Remove {{name}}',
  },
  list: {
    title: 'Tenders',
    description: 'Public tenders from Germany whose deadline has not passed yet.',
    count_one: '{{count}} tender',
    count_other: '{{count}} tenders',
    search: 'Search tenders',
    searchPlaceholder: 'Title, authority, place',
    statusAll: 'All',
    statusStock: 'Not marked',
    statusWork: 'In work',
    moreCodes_one: '(+{{count}} more)',
    moreCodes_other: '(+{{count}} more)',
    mark: 'Mark “{{title}}”',
    marked: 'Marked. You will find the tender under “My tenders”.',
    addState: 'Federal state',
    searchState: 'Search federal state',
    removeState: 'Remove {{name}}',
    within: 'Deadline within',
    withinDays_one: 'Deadline within {{count}} day',
    withinDays_other: 'Deadline within {{count}} days',
    withinAny: 'Any deadline',
    minDays: 'At least',
    minDaysValue_one: 'At least {{count}} day left',
    minDaysValue_other: 'At least {{count}} days left',
    minDaysAny: 'Any time left',
    natureAll: 'Any type',
    regulationAll: 'EU and national',
    sourceAll: 'All sources',
    published: 'Published',
    publishedDays_one: 'Since yesterday',
    publishedDays_other: 'In the last {{count}} days',
    publishedAny: 'Published any time',
    sort: 'Sort',
    sorts: {
      deadline: 'Deadline first',
      published: 'Newest first',
      relevance: 'Best matches',
    },
    range: '{{from}}–{{to}} of {{total}}',
    noMatches: 'No matching tender',
    noMatchesHint: 'Try other search terms or fewer filters.',
    empty: 'No tenders yet',
    emptyHint:
      'The import runs every morning. You can enter a tender that was sent to you directly by hand.',
  },
  mine: {
    title: 'My tenders',
    description:
      'Marked tenders and those entered by hand, from review to the answer of the buyer.',
    empty: 'Nothing in work yet',
    emptyHint: 'Mark a tender in the overview and it appears here.',
  },
  manual: {
    create: 'Enter by hand',
    createTitle: 'Enter tender',
    editTitle: 'Edit tender',
    hint: 'For example a request for an offer that reached you directly. It is in work right away, with you as the person responsible.',
    created: 'Tender entered.',
    updated: 'Changes saved.',
    conflictHint: 'Please close the dialog and reload the page.',
    noState: 'No federal state',
    importedHint:
      'What you change here stays, even if the source changes the notice later. Everything else keeps following the source.',
  },
  detail: {
    notFound: 'Tender not found',
    notFoundHint: 'It was deleted, or its deadline passed while nobody had marked it.',
    mark: 'Mark',
    markedToast: 'Marked. The tender is now in review.',
    unmark: 'Remove mark',
    unmarkedToast: 'Mark removed.',
    unmarkTitle: 'Remove mark?',
    unmarkDescription:
      'People involved, summary, files and comments are removed. The tender stays in the overview while its deadline runs.',
    deleted: 'Tender deleted.',
    deleteTitle: 'Delete tender?',
    deleteDescription:
      'The tender goes with its files and comments. An imported tender does not come back with the next import either.',
    stockTitle: 'Not marked yet',
    stockHint: 'Mark the tender to review it, keep a checklist and get reminded of the deadline.',
    stockHintReadOnly: 'Nobody is working on this tender yet.',
    closedTitle: 'No longer in the source',
    closedHint: 'The source no longer lists this tender; it was withdrawn or is finished.',
    notice: 'Notice',
    edited: 'Changed by hand: {{fields}}. The import does not overwrite these details.',
    editedFields: {
      title: 'Title',
      description: 'Description',
      buyerName: 'Contracting authority',
      placeCity: 'Place',
      state: 'Federal state',
      deadline: 'Tender deadline',
      participationDeadline: 'Participation deadline',
      documentsUrl: 'Link to the documents',
      cpv: 'CPV codes',
    },
    links: 'Links',
    documents: 'Open tender documents',
    linksHint: 'The links lead to the pages of the contracting authority or the source.',
    requirements: 'Selection criteria',
    requirementsHint: 'From the notice, as stated there.',
    lots_one: '{{count}} lot',
    lots_other: '{{count}} lots',
    lot: 'Lot {{id}}',
    lotDeadline: 'Deadline {{date}}',
  },
  people: {
    title: 'People',
    noOwner: 'Nobody responsible',
    chooseOwner: 'Choose person responsible',
    ownerChanged: 'Person responsible changed.',
    members: 'Involved',
    noMembers: 'Nobody involved yet.',
    add: 'Involve a person',
    added: 'Person involved.',
    remove: 'Remove',
    removed: 'Person removed.',
    removeTitle: 'Remove person?',
    removeDescription: 'The person no longer gets reminders about this tender.',
    feedback: 'Answer of the contracting authority',
    feedbackDue: 'Check due since {{date}}',
    feedbackNext: 'Next check on {{date}}',
    feedbackNone: 'No check planned',
    feedbackChecked: 'Checked for an answer',
    feedbackConfirmed: 'Saved. Next check on {{date}}.',
    feedbackConfirmedPlain: 'Saved.',
    feedbackHint:
      'Once the award or rejection arrives, set the status to “Won” or “Not won”. The reminders then stop.',
  },
  project: {
    title: 'Project',
    hint: 'After the award, create a project. It is then linked to the tender.',
    create: 'Create project',
    linked: 'Project linked.',
    clientTitle: 'Who is the client?',
    clientHint: 'The contracting authority is “{{name}}”. Choose the customer or create it.',
    clientHintNoBuyer: 'Choose the customer or create it.',
    client: 'Customer',
    suggested: 'Matches the name of the contracting authority.',
    notFound: 'There is no customer with this name yet.',
    next: 'Next',
  },
  summary: {
    title: 'AI summary',
    hint: 'A suggestion by the AI. Check it against the tender documents.',
    create: 'Summarise',
    again: 'Summarise again',
    created: 'Summary created.',
    empty: 'No summary yet',
    emptyHint: 'The AI summarises the notice and suggests documents for the checklist.',
    basisNotice: 'From the notice',
    createFiles: 'Summarise with documents',
    unread:
      'The AI did not read these files because they are scanned, in another format or too large for one call:',
    basisFiles: 'From the notice and documents',
    documents: 'Suggested documents',
    inChecklist: 'In the checklist',
    take_one: 'Add {{count}} point to the checklist',
    take_other: 'Add {{count}} points to the checklist',
    taken: 'Added to the checklist.',
  },
  documents: {
    title: 'Documents',
    hint: 'Tender documents and own documents. With SharePoint they are in the folder of the tender.',
    fetch: 'Load documents from the portal',
    fetched: 'Documents loaded.',
    folder: 'Create folder in SharePoint',
  },
  checklist: {
    title: 'Checklist',
    hint: 'What the submission still needs, with proof and files.',
    zip: 'All as ZIP',
    zipName: 'Submission',
    progress: '{{done}} of {{count}} done',
    empty: 'The checklist is empty',
    emptyHint: 'Add points or take over the suggestions of the AI summary.',
    emptyReadOnly: 'There are no points.',
    doneLabel: '“{{title}}” done',
    fromAi: 'AI suggestion',
    proofLinked: 'Proof linked',
    proofUnavailable: 'Proof not available',
    unlink: 'Unlink {{name}}',
    actionsFor: 'Actions for “{{title}}”',
    link: 'Link proof',
    files_one: '{{count}} file',
    files_other: '{{count}} files',
    newItem: 'New point',
    newItemPlaceholder: 'For example liability insurance',
    removed: 'Point deleted.',
    removeTitle: 'Delete point?',
    removeDescription: '“{{title}}” is deleted with its files. Linked proof stays.',
    editTitle: 'Edit point',
    itemTitle: 'Title',
    linkTitle: 'Link proof',
    linkHint: 'For “{{title}}”.',
    proof: 'Proof',
    chooseProof: 'Choose proof',
    searchProof: 'Search proof',
    noProof: 'No matching proof',
    linked: 'Proof linked.',
  },
  proof: {
    listTitle: 'Proof',
    listDescription:
      'Certificates, insurance, references and declarations you keep ready for offers.',
    create: 'New proof',
    createTitle: 'Create proof',
    createHint: 'The document itself is added as a file afterwards or lies behind the link.',
    editTitle: 'Edit proof',
    created: 'Proof created.',
    updated: 'Changes saved.',
    deleted: 'Proof deleted.',
    conflictHint: 'Please close the dialog and reload the page.',
    titlePlaceholder: 'For example business liability insurance',
    category: 'Category',
    categoryAll: 'All categories',
    chooseCategory: 'Choose category',
    searchCategory: 'Search or enter a new category',
    noCategory: 'No category yet',
    newCategory: '“{{name}}” as category',
    issuedOn: 'Issued on',
    validUntil: 'Valid until',
    validBeforeIssued: 'The end of validity is before the date of issue.',
    responsible: 'Responsible',
    link: 'Link',
    linkHint: 'For example the SharePoint folder that holds the document.',
    openLink: 'Open link',
    usage: 'Used',
    usageCount_one: 'In {{count}} checklist',
    usageCount_other: 'In {{count}} checklists',
    details: 'Details',
    changed: 'Last changed',
    search: 'Search proof',
    searchPlaceholder: 'Title, category, description',
    validityFilter: 'Validity',
    validityAll: 'Any validity',
    validityFilters: {
      valid: 'Valid',
      expiring: 'Expiring soon',
      expired: 'Expired',
    },
    validity: {
      valid: 'Valid until {{date}}',
      expiring: 'Expires on {{date}}',
      expired: 'Expired on {{date}}',
    },
    validUntilShort: 'until {{date}}',
    noExpiry: 'No expiry',
    noMatches: 'No matching proof',
    noMatchesHint: 'Try other search terms or fewer filters.',
    empty: 'No proof yet',
    emptyHint:
      'Create proof once and link it to the checklists. The app reminds you before one expires.',
    notFound: 'Proof not found',
    notFoundHint: 'It was deleted or is not visible to you.',
    expiredTitle: 'Expired',
    expiredHint: 'Upload a new document and enter the new expiry date.',
    expiringTitle: 'Expiring soon',
    expiringHint: 'Take care of a new document in good time.',
    deleteTitle: 'Delete proof?',
    deleteDescription: 'The proof is deleted with its files.',
    deleteDescriptionUsed_one:
      'The proof is deleted with its files and unlinked from {{count}} checklist.',
    deleteDescriptionUsed_other:
      'The proof is deleted with its files and unlinked from {{count}} checklists.',
  },
  sourcesPage: {
    title: 'Sources',
    description: 'Where the tenders come from and when they were last fetched.',
    settings: 'Switch sources on and off',
    run: 'Fetch all',
    runOne: 'Fetch now',
    runOneLabel: 'Fetch now: {{name}}',
    started: 'Fetch started. New tenders appear within a few minutes.',
    scheduleTitle: 'Daily fetch',
    scheduleHint:
      'The app fetches all sources switched on every morning. Tenders nobody marked disappear once their deadline has passed.',
    off: 'Switched off',
    failed: 'Error',
    ok: 'Running',
    never: 'Not fetched yet',
    lastRun: 'Last fetch',
    lastDay: 'Fetched up to',
    lastCount: 'New or changed last time',
    errorTitle: 'The last fetch failed',
    about: {
      oev: 'Notices of federal, state and municipal buyers from the Datenservice Öffentlicher Einkauf.',
      nrw: 'Open data of the procurement portals in North Rhine-Westphalia and of the state building authority BLB NRW, national tenders included.',
      ted: 'EU-wide notices from the Official Journal of the EU with a buyer or place of performance in Germany, with CPV codes.',
      vkberlin:
        'Tenders of BVG, Berliner Wasserbetriebe, BSR, Messe Berlin, Forschungsverbund Berlin and others, informal procedures included, without CPV codes.',
      berlin:
        'The 50 latest notices of the procurement platform of the State of Berlin, without CPV codes.',
      bund: 'Latest tenders from the federal portal, without CPV codes.',
    },
    licence: {
      oev: 'Data: Datenservice Öffentlicher Einkauf, oeffentlichevergabe.de.',
      nrw: 'Data: NRW procurement portals and BLB NRW via open.nrw and GovData, licence dl-de/zero-2.0.',
      ted: 'Data: Publications Office of the EU, ted.europa.eu, free to reuse (Decision 2011/833/EU).',
      berlin: 'Data: procurement platform of the State of Berlin, berlin.de.',
      vkberlin:
        'Data: Vergabekooperation Berlin, Administration Intelligence AG, vergabekooperation.berlin.',
      bund: 'Data: service.bund.de.',
    },
  },
};
